import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Plus, Trash2, RotateCcw, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTIONS = [
  { key: 'hero', label: 'Hero' },
  { key: 'products', label: 'Lösungen' },
  { key: 'services', label: 'Leistungen' },
  { key: 'projects', label: 'Projekte' },
  { key: 'testimonials', label: 'Kundenstimmen' },
  { key: 'pricing', label: 'Preise' },
  { key: 'legal_privacy', label: 'Datenschutz' },
  { key: 'legal_terms', label: 'AGB' },
  { key: 'legal_imprint', label: 'Impressum' },
];

const inputCls = 'bg-[#F5F5F7] border-0 text-[#1D1D1F] rounded-xl h-11';
const textareaCls = 'bg-[#F5F5F7] border-0 text-[#1D1D1F] rounded-xl';
const labelCls = 'block text-sm font-medium text-[#1D1D1F] mb-2';
const cardCls = 'bg-[#F5F5F7] rounded-2xl p-5 space-y-4';

// ---------- Hero ----------
const HeroEditor = ({ data, onChange }) => {
  const setField = (field, value) => onChange({ ...data, [field]: value });
  const setStat = (idx, field, value) => {
    const stats = [...(data.stats || [])];
    stats[idx] = { ...stats[idx], [field]: value };
    onChange({ ...data, stats });
  };
  const addStat = () => onChange({ ...data, stats: [...(data.stats || []), { value: '', label: '' }] });
  const removeStat = (idx) => {
    const stats = [...(data.stats || [])];
    stats.splice(idx, 1);
    onChange({ ...data, stats });
  };

  return (
    <div className="space-y-5">
      <div>
        <label className={labelCls}>Eyebrow (kleine Zeile über dem Titel)</label>
        <Input className={inputCls} value={data.eyebrow || ''} onChange={(e) => setField('eyebrow', e.target.value)} />
      </div>
      <div>
        <label className={labelCls}>Titel – Zeile 1</label>
        <Input className={inputCls} value={data.title_part1 || ''} onChange={(e) => setField('title_part1', e.target.value)} data-testid="hero-edit-title1" />
      </div>
      <div>
        <label className={labelCls}>Titel – Zeile 2 (graue Hervorhebung)</label>
        <Input className={inputCls} value={data.title_part2 || ''} onChange={(e) => setField('title_part2', e.target.value)} data-testid="hero-edit-title2" />
      </div>
      <div>
        <label className={labelCls}>Untertitel – Zeile 1</label>
        <Input className={inputCls} value={data.subtitle_line1 || ''} onChange={(e) => setField('subtitle_line1', e.target.value)} />
      </div>
      <div>
        <label className={labelCls}>Untertitel – Zeile 2</label>
        <Input className={inputCls} value={data.subtitle_line2 || ''} onChange={(e) => setField('subtitle_line2', e.target.value)} />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Primärer Button</label>
          <Input className={inputCls} value={data.cta_primary_label || ''} onChange={(e) => setField('cta_primary_label', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Sekundärer Button</label>
          <Input className={inputCls} value={data.cta_secondary_label || ''} onChange={(e) => setField('cta_secondary_label', e.target.value)} />
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-2">
          <label className={labelCls + ' mb-0'}>Statistiken</label>
          <Button type="button" size="sm" variant="outline" onClick={addStat} className="rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Neue Statistik
          </Button>
        </div>
        <div className="space-y-3">
          {(data.stats || []).map((stat, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end">
              <div>
                <label className="text-xs text-[#86868B]">Wert</label>
                <Input className={inputCls} value={stat.value || ''} onChange={(e) => setStat(idx, 'value', e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-[#86868B]">Beschriftung</label>
                <Input className={inputCls} value={stat.label || ''} onChange={(e) => setStat(idx, 'label', e.target.value)} />
              </div>
              <Button type="button" variant="outline" onClick={() => removeStat(idx)} className="h-11 w-11 p-0 rounded-full">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className={labelCls}>Kundenleiste – Titel</label>
        <Input className={inputCls} value={data.customers_label || ''} onChange={(e) => setField('customers_label', e.target.value)} />
      </div>
      <div>
        <label className={labelCls}>Kundenleiste – Namen</label>
        <StringListEditor
          values={data.customers || []}
          onChange={(vs) => onChange({ ...data, customers: vs })}
          placeholder="Kundenname"
        />
      </div>
    </div>
  );
};

// ---------- Services ----------
const ICON_OPTIONS = ['Code', 'Zap', 'Database', 'TrendingUp', 'Cloud', 'Cpu', 'Shield', 'Sparkles', 'Rocket', 'Settings', 'Globe', 'Smartphone'];

const ServicesEditor = ({ data, onChange }) => {
  const setField = (field, value) => onChange({ ...data, [field]: value });

  const setItem = (idx, field, value) => {
    const items = [...(data.items || [])];
    items[idx] = { ...items[idx], [field]: value };
    onChange({ ...data, items });
  };
  const addItem = () => {
    const items = [...(data.items || [])];
    const nextId = items.reduce((m, it) => Math.max(m, it.id || 0), 0) + 1;
    items.push({ id: nextId, icon: 'Code', title: 'Neuer Service', description: '' });
    onChange({ ...data, items });
  };
  const removeItem = (idx) => {
    const items = [...(data.items || [])];
    items.splice(idx, 1);
    onChange({ ...data, items });
  };

  const setHighlight = (idx, field, value) => {
    const highlights = [...(data.highlights || [])];
    highlights[idx] = { ...highlights[idx], [field]: value };
    onChange({ ...data, highlights });
  };
  const addHighlight = () => onChange({ ...data, highlights: [...(data.highlights || []), { title: '', description: '' }] });
  const removeHighlight = (idx) => {
    const highlights = [...(data.highlights || [])];
    highlights.splice(idx, 1);
    onChange({ ...data, highlights });
  };

  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Sektions-Titel</label>
          <Input className={inputCls} value={data.section_title || ''} onChange={(e) => setField('section_title', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Sektions-Untertitel</label>
          <Input className={inputCls} value={data.section_subtitle || ''} onChange={(e) => setField('section_subtitle', e.target.value)} />
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-base font-semibold text-[#1D1D1F]">Services</h3>
          <Button type="button" size="sm" variant="outline" onClick={addItem} className="rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Neuer Service
          </Button>
        </div>
        <div className="space-y-4">
          {(data.items || []).map((item, idx) => (
            <div key={item.id || idx} className={cardCls}>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#86868B]">Service #{idx + 1}</span>
                <Button type="button" variant="outline" onClick={() => removeItem(idx)} className="h-9 w-9 p-0 rounded-full">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#86868B]">Titel</label>
                  <Input className={inputCls} value={item.title || ''} onChange={(e) => setItem(idx, 'title', e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-[#86868B]">Icon</label>
                  <select
                    value={item.icon || 'Code'}
                    onChange={(e) => setItem(idx, 'icon', e.target.value)}
                    className="w-full bg-white border-0 text-[#1D1D1F] rounded-xl h-11 px-3"
                  >
                    {ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-[#86868B]">Beschreibung</label>
                <Textarea rows={2} className={textareaCls} value={item.description || ''} onChange={(e) => setItem(idx, 'description', e.target.value)} />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#86868B]">Bild-URL (optional, ersetzt Icon)</label>
                  <Input className={inputCls} value={item.image || ''} onChange={(e) => setItem(idx, 'image', e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-[#86868B]">Beispiel-Kunde (optional)</label>
                  <Input className={inputCls} value={item.example || ''} onChange={(e) => setItem(idx, 'example', e.target.value)} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Schritte – Titel</label>
          <Input className={inputCls} value={data.steps_title || ''} onChange={(e) => setField('steps_title', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Schritte – Untertitel</label>
          <Input className={inputCls} value={data.steps_subtitle || ''} onChange={(e) => setField('steps_subtitle', e.target.value)} />
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-base font-semibold text-[#1D1D1F]">Schritte («So einfach geht's»)</h3>
          <Button type="button" size="sm" variant="outline" onClick={addHighlight} className="rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Neues Highlight
          </Button>
        </div>
        <div className="space-y-3">
          {(data.highlights || []).map((h, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_2fr_auto] gap-3 items-end">
              <div>
                <label className="text-xs text-[#86868B]">Titel</label>
                <Input className={inputCls} value={h.title || ''} onChange={(e) => setHighlight(idx, 'title', e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-[#86868B]">Beschreibung</label>
                <Input className={inputCls} value={h.description || ''} onChange={(e) => setHighlight(idx, 'description', e.target.value)} />
              </div>
              <Button type="button" variant="outline" onClick={() => removeHighlight(idx)} className="h-11 w-11 p-0 rounded-full">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------- Projects ----------
const StringListEditor = ({ values, onChange, placeholder }) => {
  const setVal = (idx, v) => {
    const next = [...values];
    next[idx] = v;
    onChange(next);
  };
  return (
    <div className="space-y-2">
      {(values || []).map((v, idx) => (
        <div key={idx} className="flex gap-2">
          <Input className={inputCls} value={v} onChange={(e) => setVal(idx, e.target.value)} placeholder={placeholder} />
          <Button type="button" variant="outline" onClick={() => onChange(values.filter((_, i) => i !== idx))} className="h-11 w-11 p-0 rounded-full">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...(values || []), ''])} className="rounded-full">
        <Plus className="w-4 h-4 mr-1" /> Eintrag
      </Button>
    </div>
  );
};

const ProjectsEditor = ({ data, onChange }) => {
  const setField = (field, value) => onChange({ ...data, [field]: value });

  const setItem = (idx, field, value) => {
    const items = [...(data.items || [])];
    items[idx] = { ...items[idx], [field]: value };
    onChange({ ...data, items });
  };
  const addItem = () => {
    const items = [...(data.items || [])];
    const nextId = items.reduce((m, it) => Math.max(m, it.id || 0), 0) + 1;
    items.push({
      id: nextId,
      title: 'Neues Projekt',
      description: '',
      category: '',
      features: [],
      technologies: [],
      image: '',
      liveUrl: '',
      year: String(new Date().getFullYear()),
    });
    onChange({ ...data, items });
  };
  const removeItem = (idx) => {
    const items = [...(data.items || [])];
    items.splice(idx, 1);
    onChange({ ...data, items });
  };

  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Sektions-Titel</label>
          <Input className={inputCls} value={data.section_title || ''} onChange={(e) => setField('section_title', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Sektions-Untertitel</label>
          <Input className={inputCls} value={data.section_subtitle || ''} onChange={(e) => setField('section_subtitle', e.target.value)} />
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-base font-semibold text-[#1D1D1F]">Projekte</h3>
          <Button type="button" size="sm" variant="outline" onClick={addItem} className="rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Neues Projekt
          </Button>
        </div>

        <div className="space-y-4">
          {(data.items || []).map((item, idx) => (
            <div key={item.id || idx} className={cardCls}>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-[#1D1D1F]">{item.title || 'Neues Projekt'}</span>
                <Button type="button" variant="outline" onClick={() => removeItem(idx)} className="h-9 w-9 p-0 rounded-full">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#86868B]">Titel</label>
                  <Input className={inputCls} value={item.title || ''} onChange={(e) => setItem(idx, 'title', e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-[#86868B]">Kategorie</label>
                  <Input className={inputCls} value={item.category || ''} onChange={(e) => setItem(idx, 'category', e.target.value)} />
                </div>
              </div>

              <div>
                <label className="text-xs text-[#86868B]">Beschreibung</label>
                <Textarea rows={3} className={textareaCls} value={item.description || ''} onChange={(e) => setItem(idx, 'description', e.target.value)} />
              </div>

              <div className="grid md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <label className="text-xs text-[#86868B]">Bild-URL</label>
                  <Input className={inputCls} value={item.image || ''} onChange={(e) => setItem(idx, 'image', e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-[#86868B]">Jahr</label>
                  <Input className={inputCls} value={item.year || ''} onChange={(e) => setItem(idx, 'year', e.target.value)} />
                </div>
              </div>

              <div>
                <label className="text-xs text-[#86868B]">Live-URL</label>
                <Input className={inputCls} value={item.liveUrl || ''} onChange={(e) => setItem(idx, 'liveUrl', e.target.value)} />
                <label className="flex items-center gap-2 mt-2 text-sm text-[#1D1D1F] cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={item.showLiveUrl !== false}
                    onChange={(e) => setItem(idx, 'showLiveUrl', e.target.checked)}
                    className="w-4 h-4 accent-[#0071E3]"
                    data-testid={`project-showurl-${idx}`}
                  />
                  <span>„Live Demo"-Button öffentlich anzeigen</span>
                </label>
                <p className="text-xs text-[#86868B] mt-1 pl-6">
                  Deaktivieren: URL bleibt intern gespeichert, Besucher sehen den Button nicht.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-[#86868B]">Funktionen</label>
                  <StringListEditor
                    values={item.features || []}
                    onChange={(vs) => setItem(idx, 'features', vs)}
                    placeholder="Funktion"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#86868B]">Technologien</label>
                  <StringListEditor
                    values={item.technologies || []}
                    onChange={(vs) => setItem(idx, 'technologies', vs)}
                    placeholder="Technologie"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------- Testimonials ----------
const TestimonialsEditor = ({ data, onChange }) => {
  const setField = (field, value) => onChange({ ...data, [field]: value });
  const setItem = (idx, field, value) => {
    const items = [...(data.items || [])];
    items[idx] = { ...items[idx], [field]: value };
    onChange({ ...data, items });
  };
  const addItem = () => {
    const items = [...(data.items || [])];
    const nextId = items.reduce((m, it) => Math.max(m, it.id || 0), 0) + 1;
    items.push({ id: nextId, quote: '', name: '', role: '' });
    onChange({ ...data, items });
  };
  const removeItem = (idx) => {
    const items = [...(data.items || [])];
    items.splice(idx, 1);
    onChange({ ...data, items });
  };

  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Sektions-Titel</label>
          <Input className={inputCls} value={data.section_title || ''} onChange={(e) => setField('section_title', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Sektions-Untertitel</label>
          <Input className={inputCls} value={data.section_subtitle || ''} onChange={(e) => setField('section_subtitle', e.target.value)} />
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-base font-semibold text-[#1D1D1F]">Kundenstimmen</h3>
          <Button type="button" size="sm" variant="outline" onClick={addItem} className="rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Neue Kundenstimme
          </Button>
        </div>
        <div className="space-y-4">
          {(data.items || []).map((item, idx) => (
            <div key={item.id || idx} className={cardCls}>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#86868B]">Kundenstimme #{idx + 1}</span>
                <Button type="button" variant="outline" onClick={() => removeItem(idx)} className="h-9 w-9 p-0 rounded-full">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div>
                <label className="text-xs text-[#86868B]">Zitat</label>
                <Textarea rows={3} className={textareaCls} value={item.quote || ''} onChange={(e) => setItem(idx, 'quote', e.target.value)} />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#86868B]">Name</label>
                  <Input className={inputCls} value={item.name || ''} onChange={(e) => setItem(idx, 'name', e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-[#86868B]">Firma / Rolle</label>
                  <Input className={inputCls} value={item.role || ''} onChange={(e) => setItem(idx, 'role', e.target.value)} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------- Pricing ----------
const PricingEditor = ({ data, onChange }) => {
  const setField = (field, value) => onChange({ ...data, [field]: value });
  const setSetup = (field, value) => onChange({ ...data, setup: { ...(data.setup || {}), [field]: value } });
  const setMonthly = (field, value) => onChange({ ...data, monthly: { ...(data.monthly || {}), [field]: value } });

  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Sektions-Titel</label>
          <Input className={inputCls} value={data.section_title || ''} onChange={(e) => setField('section_title', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Sektions-Untertitel</label>
          <Input className={inputCls} value={data.section_subtitle || ''} onChange={(e) => setField('section_subtitle', e.target.value)} />
        </div>
      </div>

      <div className={cardCls}>
        <h3 className="text-base font-semibold text-[#1D1D1F]">Setup-Pauschale</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-[#86868B]">Preis</label>
            <Input className={inputCls} value={data.setup?.price || ''} onChange={(e) => setSetup('price', e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-[#86868B]">Währung</label>
            <Input className={inputCls} value={data.setup?.currency || ''} onChange={(e) => setSetup('currency', e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-[#86868B]">Beschreibung</label>
            <Input className={inputCls} value={data.setup?.description || ''} onChange={(e) => setSetup('description', e.target.value)} />
          </div>
        </div>
        <div>
          <label className="text-xs text-[#86868B]">Inkludiert (Liste)</label>
          <StringListEditor values={data.setup?.includes || []} onChange={(vs) => setSetup('includes', vs)} placeholder="Inhalt" />
        </div>
        <div>
          <label className="text-xs text-[#86868B]">Fussnote</label>
          <Textarea rows={2} className={textareaCls} value={data.setup?.footnote || ''} onChange={(e) => setSetup('footnote', e.target.value)} />
        </div>
      </div>

      <div className={cardCls}>
        <h3 className="text-base font-semibold text-[#1D1D1F]">Monatliche Kosten</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-[#86868B]">Preis ab</label>
            <Input className={inputCls} value={data.monthly?.priceFrom || ''} onChange={(e) => setMonthly('priceFrom', e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-[#86868B]">Währung</label>
            <Input className={inputCls} value={data.monthly?.currency || ''} onChange={(e) => setMonthly('currency', e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-[#86868B]">Beschreibung</label>
            <Input className={inputCls} value={data.monthly?.description || ''} onChange={(e) => setMonthly('description', e.target.value)} />
          </div>
        </div>
        <div>
          <label className="text-xs text-[#86868B]">Hinweis</label>
          <Input className={inputCls} value={data.monthly?.note || ''} onChange={(e) => setMonthly('note', e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-[#86868B]">Inkludiert (Liste)</label>
          <StringListEditor values={data.monthly?.includes || []} onChange={(vs) => setMonthly('includes', vs)} placeholder="Inhalt" />
        </div>
        <div>
          <label className="text-xs text-[#86868B]">Fussnote</label>
          <Textarea rows={2} className={textareaCls} value={data.monthly?.footnote || ''} onChange={(e) => setMonthly('footnote', e.target.value)} />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>CTA-Titel</label>
          <Input className={inputCls} value={data.cta_title || ''} onChange={(e) => setField('cta_title', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>CTA-Button</label>
          <Input className={inputCls} value={data.cta_button || ''} onChange={(e) => setField('cta_button', e.target.value)} />
        </div>
      </div>
      <div>
        <label className={labelCls}>CTA-Untertitel</label>
        <Input className={inputCls} value={data.cta_subtitle || ''} onChange={(e) => setField('cta_subtitle', e.target.value)} />
      </div>
    </div>
  );
};

// ---------- Legal ----------
const LegalEditor = ({ data, onChange }) => {
  const setField = (field, value) => onChange({ ...data, [field]: value });
  const setSection = (idx, field, value) => {
    const sections = [...(data.sections || [])];
    sections[idx] = { ...sections[idx], [field]: value };
    onChange({ ...data, sections });
  };
  const addSection = () => onChange({ ...data, sections: [...(data.sections || []), { heading: '', subheading: '', body: '' }] });
  const removeSection = (idx) => {
    const sections = [...(data.sections || [])];
    sections.splice(idx, 1);
    onChange({ ...data, sections });
  };
  const moveSection = (idx, dir) => {
    const sections = [...(data.sections || [])];
    const ni = idx + dir;
    if (ni < 0 || ni >= sections.length) return;
    [sections[idx], sections[ni]] = [sections[ni], sections[idx]];
    onChange({ ...data, sections });
  };

  return (
    <div className="space-y-5">
      <div>
        <label className={labelCls}>Titel</label>
        <Input className={inputCls} value={data.title || ''} onChange={(e) => setField('title', e.target.value)} />
      </div>

      <div>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-base font-semibold text-[#1D1D1F]">Abschnitte</h3>
          <Button type="button" size="sm" variant="outline" onClick={addSection} className="rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Neuer Abschnitt
          </Button>
        </div>

        <div className="space-y-4">
          {(data.sections || []).map((section, idx) => (
            <div key={idx} className={cardCls}>
              <div className="flex justify-between items-center">
                <span className="text-xs text-[#86868B]">Abschnitt #{idx + 1}</span>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => moveSection(idx, -1)} className="h-8 px-3 rounded-full text-xs">↑</Button>
                  <Button type="button" variant="outline" onClick={() => moveSection(idx, 1)} className="h-8 px-3 rounded-full text-xs">↓</Button>
                  <Button type="button" variant="outline" onClick={() => removeSection(idx)} className="h-8 w-8 p-0 rounded-full">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-xs text-[#86868B]">Überschrift</label>
                <Input className={inputCls} value={section.heading || ''} onChange={(e) => setSection(idx, 'heading', e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-[#86868B]">Unterüberschrift (optional)</label>
                <Input className={inputCls} value={section.subheading || ''} onChange={(e) => setSection(idx, 'subheading', e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-[#86868B]">Inhalt (Bullet-Punkte mit „- " am Zeilenanfang, Absätze mit Leerzeile)</label>
                <Textarea rows={6} className={textareaCls} value={section.body || ''} onChange={(e) => setSection(idx, 'body', e.target.value)} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------- Detail-Abschnitte (Titel + Funktionen mit Beschreibung) ----------
const DetailSectionsEditor = ({ sections, onChange }) => {
  const setSection = (idx, field, value) => {
    const next = [...(sections || [])];
    next[idx] = { ...next[idx], [field]: value };
    onChange(next);
  };
  const setFeature = (idx, fIdx, field, value) => {
    const next = [...(sections || [])];
    const features = [...(next[idx].features || [])];
    features[fIdx] = { ...features[fIdx], [field]: value };
    next[idx] = { ...next[idx], features };
    onChange(next);
  };
  const addFeature = (idx) => {
    const next = [...(sections || [])];
    next[idx] = { ...next[idx], features: [...(next[idx].features || []), { text: '', desc: '' }] };
    onChange(next);
  };
  const removeFeature = (idx, fIdx) => {
    const next = [...(sections || [])];
    const features = [...(next[idx].features || [])];
    features.splice(fIdx, 1);
    next[idx] = { ...next[idx], features };
    onChange(next);
  };
  const addSection = () => onChange([...(sections || []), { title: '', features: [] }]);
  const removeSection = (idx) => {
    const next = [...(sections || [])];
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div className="space-y-4">
      {(sections || []).map((section, idx) => (
        <div key={idx} className="border border-[#E8E8ED] rounded-2xl p-4 space-y-3 bg-white">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="text-xs text-[#86868B]">Abschnitts-Titel</label>
              <Input className={inputCls} value={section.title || ''} onChange={(e) => setSection(idx, 'title', e.target.value)} />
            </div>
            <Button type="button" variant="outline" onClick={() => removeSection(idx)} className="h-9 w-9 p-0 rounded-full mt-4">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-2">
            {(section.features || []).map((f, fIdx) => (
              <div key={fIdx} className="grid grid-cols-[200px_1fr_auto] gap-2 items-start">
                <Input className={inputCls} placeholder="Funktion" value={f.text || ''} onChange={(e) => setFeature(idx, fIdx, 'text', e.target.value)} />
                <Textarea rows={2} className={textareaCls} placeholder="Beschreibung" value={f.desc || ''} onChange={(e) => setFeature(idx, fIdx, 'desc', e.target.value)} />
                <Button type="button" variant="outline" onClick={() => removeFeature(idx, fIdx)} className="h-9 w-9 p-0 rounded-full">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" onClick={() => addFeature(idx)} className="rounded-full">
              <Plus className="w-4 h-4 mr-1" /> Funktion
            </Button>
          </div>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={addSection} className="rounded-full">
        <Plus className="w-4 h-4 mr-1" /> Neuer Abschnitt
      </Button>
    </div>
  );
};

// ---------- Products (Lösungen) ----------
const ProductsEditor = ({ data, onChange }) => {
  const setField = (field, value) => onChange({ ...data, [field]: value });
  const setItem = (idx, field, value) => {
    const items = [...(data.items || [])];
    items[idx] = { ...items[idx], [field]: value };
    onChange({ ...data, items });
  };
  const setFeature = (idx, fIdx, field, value) => {
    const items = [...(data.items || [])];
    const features = [...(items[idx].features || [])];
    features[fIdx] = { ...features[fIdx], [field]: value };
    items[idx] = { ...items[idx], features };
    onChange({ ...data, items });
  };
  const addFeature = (idx) => {
    const items = [...(data.items || [])];
    items[idx] = { ...items[idx], features: [...(items[idx].features || []), { text: '', coming_soon: false }] };
    onChange({ ...data, items });
  };
  const removeFeature = (idx, fIdx) => {
    const items = [...(data.items || [])];
    const features = [...(items[idx].features || [])];
    features.splice(fIdx, 1);
    items[idx] = { ...items[idx], features };
    onChange({ ...data, items });
  };
  const setNote = (field, value) =>
    onChange({ ...data, platform_note: { ...(data.platform_note || {}), [field]: value } });

  return (
    <div className="space-y-6">
      <div>
        <label className={labelCls}>Eyebrow (kleine Zeile über dem Titel)</label>
        <Input className={inputCls} value={data.eyebrow || ''} onChange={(e) => setField('eyebrow', e.target.value)} />
      </div>
      <div>
        <label className={labelCls}>Titel</label>
        <Input className={inputCls} value={data.section_title || ''} onChange={(e) => setField('section_title', e.target.value)} />
      </div>
      <div>
        <label className={labelCls}>Untertitel</label>
        <Textarea rows={2} className={textareaCls} value={data.section_subtitle || ''} onChange={(e) => setField('section_subtitle', e.target.value)} />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>Button (live) – {'{name}'} = Produktname</label>
          <Input className={inputCls} value={data.cta_live || ''} onChange={(e) => setField('cta_live', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Button (nicht live)</label>
          <Input className={inputCls} value={data.cta_soon || ''} onChange={(e) => setField('cta_soon', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Badge «Bald verfügbar»</label>
          <Input className={inputCls} value={data.badge_soon || ''} onChange={(e) => setField('badge_soon', e.target.value)} />
        </div>
      </div>

      {(data.items || []).map((item, idx) => (
        <div key={item.id || idx} className={cardCls}>
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-[#1D1D1F]">{item.name || `Produkt ${idx + 1}`}</h3>
            <label className="flex items-center gap-2 text-sm text-[#1D1D1F] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!item.subdomain_live}
                onChange={(e) => setItem(idx, 'subdomain_live', e.target.checked)}
                className="w-4 h-4 accent-[#0071E3]"
                data-testid={`product-live-toggle-${item.id}`}
              />
              Subdomain live (Button verlinkt direkt)
            </label>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[#86868B]">Name</label>
              <Input className={inputCls} value={item.name || ''} onChange={(e) => setItem(idx, 'name', e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-[#86868B]">Link zur Subdomain</label>
              <Input className={inputCls} value={item.url || ''} onChange={(e) => setItem(idx, 'url', e.target.value)} />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#86868B]">Logo (Bild-URL oder Pfad, z.B. /logos/care-icon.png – leer = Standard-Icon)</label>
            <Input className={inputCls} value={item.logo || ''} onChange={(e) => setItem(idx, 'logo', e.target.value)} data-testid={`product-logo-input-${item.id}`} />
          </div>
          <div>
            <label className="text-xs text-[#86868B]">Claim</label>
            <Input className={inputCls} value={item.claim || ''} onChange={(e) => setItem(idx, 'claim', e.target.value)} />
          </div>
          <div className="grid md:grid-cols-[1fr_140px] gap-4">
            <div>
              <label className="text-xs text-[#86868B]">Zielgruppe</label>
              <Input className={inputCls} value={item.audience || ''} onChange={(e) => setItem(idx, 'audience', e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-[#86868B]">Akzentfarbe (Hex)</label>
              <Input className={inputCls} value={item.color || ''} onChange={(e) => setItem(idx, 'color', e.target.value)} />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#86868B]">Preis-Teaser (z.B. «ab CHF 149.–/Monat» – leer lassen = ausgeblendet)</label>
            <Input
              className={inputCls}
              value={item.price_from || ''}
              onChange={(e) => setItem(idx, 'price_from', e.target.value)}
              data-testid={`product-price-input-${item.id}`}
            />
          </div>
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-xs text-[#86868B]">Funktionen (Häkchen = «Bald verfügbar»-Badge)</label>
              <Button type="button" size="sm" variant="outline" onClick={() => addFeature(idx)} className="rounded-full">
                <Plus className="w-4 h-4 mr-1" /> Funktion
              </Button>
            </div>
            <div className="space-y-2">
              {(item.features || []).map((f, fIdx) => (
                <div key={fIdx} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center">
                  <Input className={inputCls} value={f.text || ''} onChange={(e) => setFeature(idx, fIdx, 'text', e.target.value)} />
                  <label className="flex items-center gap-1.5 text-xs text-[#86868B] cursor-pointer select-none whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={!!f.coming_soon}
                      onChange={(e) => setFeature(idx, fIdx, 'coming_soon', e.target.checked)}
                      className="w-4 h-4 accent-[#0071E3]"
                    />
                    Bald verfügbar
                  </label>
                  <Button type="button" variant="outline" onClick={() => removeFeature(idx, fIdx)} className="h-9 w-9 p-0 rounded-full">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <details className="border border-[#E8E8ED] rounded-2xl p-4 bg-[#FAFAFA]">
            <summary className="text-sm font-medium text-[#1D1D1F] cursor-pointer select-none">
              Detail-Abschnitte auf der Produktseite ({(item.detail_sections || []).length})
            </summary>
            <div className="mt-4">
              <DetailSectionsEditor
                sections={item.detail_sections || []}
                onChange={(sections) => setItem(idx, 'detail_sections', sections)}
              />
            </div>
          </details>
        </div>
      ))}

      <div className={cardCls}>
        <h3 className="text-base font-semibold text-[#1D1D1F]">Gründer-Angebot (erste 50 Anmeldungen)</h3>
        <label className="flex items-center gap-2 text-sm text-[#1D1D1F] cursor-pointer select-none">
          <input
            type="checkbox"
            checked={!!(data.founders_offer || {}).enabled}
            onChange={(e) => setField('founders_offer', { ...(data.founders_offer || {}), enabled: e.target.checked })}
            className="w-4 h-4 accent-[#0071E3]"
            data-testid="founders-offer-toggle"
          />
          Angebot anzeigen (Produktseiten + Pilotkunden-Formular)
        </label>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#86868B]">Badge</label>
            <Input className={inputCls} value={(data.founders_offer || {}).badge || ''} onChange={(e) => setField('founders_offer', { ...(data.founders_offer || {}), badge: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-[#86868B]">Titel</label>
            <Input className={inputCls} value={(data.founders_offer || {}).title || ''} onChange={(e) => setField('founders_offer', { ...(data.founders_offer || {}), title: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-[#86868B]">Preis (z.B. CHF 100.–)</label>
            <Input className={inputCls} value={(data.founders_offer || {}).price || ''} onChange={(e) => setField('founders_offer', { ...(data.founders_offer || {}), price: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-[#86868B]">Preis-Zusatz (z.B. pro Monat im 1. Jahr)</label>
            <Input className={inputCls} value={(data.founders_offer || {}).price_period || ''} onChange={(e) => setField('founders_offer', { ...(data.founders_offer || {}), price_period: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-[#86868B]">Danach-Preis (z.B. danach CHF 250.– pro Monat)</label>
            <Input className={inputCls} value={(data.founders_offer || {}).after_price || ''} onChange={(e) => setField('founders_offer', { ...(data.founders_offer || {}), after_price: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-[#86868B]">Limit-Hinweis</label>
            <Input className={inputCls} value={(data.founders_offer || {}).limit_note || ''} onChange={(e) => setField('founders_offer', { ...(data.founders_offer || {}), limit_note: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-[#86868B]">Button-Text</label>
            <Input className={inputCls} value={(data.founders_offer || {}).cta || ''} onChange={(e) => setField('founders_offer', { ...(data.founders_offer || {}), cta: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="text-xs text-[#86868B]">Beschreibung</label>
          <Textarea rows={3} className={textareaCls} value={(data.founders_offer || {}).text || ''} onChange={(e) => setField('founders_offer', { ...(data.founders_offer || {}), text: e.target.value })} />
        </div>
      </div>

      <div className={cardCls}>
        <h3 className="text-base font-semibold text-[#1D1D1F]">Produktseiten: Überschriften</h3>
        <div>
          <label className="text-xs text-[#86868B]">Titel «Alle Funktionen im Detail»</label>
          <Input className={inputCls} value={data.detail_title || ''} onChange={(e) => setField('detail_title', e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-[#86868B]">Titel Plattform-Basis</label>
          <Input className={inputCls} value={data.shared_title || ''} onChange={(e) => setField('shared_title', e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-[#86868B]">Untertitel Plattform-Basis</label>
          <Textarea rows={2} className={textareaCls} value={data.shared_subtitle || ''} onChange={(e) => setField('shared_subtitle', e.target.value)} />
        </div>
        <details className="border border-[#E8E8ED] rounded-2xl p-4 bg-[#FAFAFA]">
          <summary className="text-sm font-medium text-[#1D1D1F] cursor-pointer select-none">
            Plattform-Basis Abschnitte – auf allen Produktseiten ({(data.shared_sections || []).length})
          </summary>
          <div className="mt-4">
            <DetailSectionsEditor
              sections={data.shared_sections || []}
              onChange={(sections) => setField('shared_sections', sections)}
            />
          </div>
        </details>
      </div>

      <div className={cardCls}>
        <h3 className="text-base font-semibold text-[#1D1D1F]">Block «So funktioniert die Plattform»</h3>
        <div>
          <label className="text-xs text-[#86868B]">Titel</label>
          <Input
            className={inputCls}
            value={(data.platform_details || {}).title || ''}
            onChange={(e) => setField('platform_details', { ...(data.platform_details || {}), title: e.target.value })}
          />
        </div>
        <div className="space-y-3">
          {((data.platform_details || {}).items || []).map((d, i) => (
            <div key={i} className="grid grid-cols-[220px_1fr_auto] gap-2 items-start">
              <Input
                className={inputCls}
                placeholder="Titel"
                value={d.title || ''}
                onChange={(e) => {
                  const items = [...((data.platform_details || {}).items || [])];
                  items[i] = { ...items[i], title: e.target.value };
                  setField('platform_details', { ...(data.platform_details || {}), items });
                }}
              />
              <Textarea
                rows={2}
                className={textareaCls}
                placeholder="Text"
                value={d.text || ''}
                onChange={(e) => {
                  const items = [...((data.platform_details || {}).items || [])];
                  items[i] = { ...items[i], text: e.target.value };
                  setField('platform_details', { ...(data.platform_details || {}), items });
                }}
              />
              <Button
                type="button"
                variant="outline"
                className="h-9 w-9 p-0 rounded-full"
                onClick={() => {
                  const items = [...((data.platform_details || {}).items || [])];
                  items.splice(i, 1);
                  setField('platform_details', { ...(data.platform_details || {}), items });
                }}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-full"
            onClick={() =>
              setField('platform_details', {
                ...(data.platform_details || {}),
                items: [...((data.platform_details || {}).items || []), { title: '', text: '' }],
              })
            }
          >
            <Plus className="w-4 h-4 mr-1" /> Punkt
          </Button>
        </div>
      </div>

      <div className={cardCls}>
        <h3 className="text-base font-semibold text-[#1D1D1F]">Block «Eine Plattform»</h3>
        <div>
          <label className="text-xs text-[#86868B]">Titel</label>
          <Input className={inputCls} value={(data.platform_note || {}).title || ''} onChange={(e) => setNote('title', e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-[#86868B]">Text</label>
          <Textarea rows={2} className={textareaCls} value={(data.platform_note || {}).text || ''} onChange={(e) => setNote('text', e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-[#86868B]">Punkte</label>
          <StringListEditor
            values={(data.platform_note || {}).points || []}
            onChange={(vs) => setNote('points', vs)}
            placeholder="Punkt"
          />
        </div>
      </div>
    </div>
  );
};

const EDITOR_MAP = {
  hero: HeroEditor,
  products: ProductsEditor,
  services: ServicesEditor,
  projects: ProjectsEditor,
  testimonials: TestimonialsEditor,
  pricing: PricingEditor,
  legal_privacy: LegalEditor,
  legal_terms: LegalEditor,
  legal_imprint: LegalEditor,
};

const ContentEditor = () => {
  const [activeKey, setActiveKey] = useState('hero');
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const authHeaders = { Authorization: `Bearer ${token || ''}` };

  const loadAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/content`);
      setContent(res.data);
    } catch (err) {
      console.error(err);
      setMessage('❌ Inhalte konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await axios.put(
        `${API}/admin/content/${activeKey}`,
        { data: content[activeKey] },
        { headers: authHeaders }
      );
      setMessage('✅ Gespeichert. Änderungen sind nach dem nächsten Seiten-Reload sichtbar.');
      setTimeout(() => setMessage(''), 4000);
    } catch (err) {
      console.error(err);
      setMessage('❌ Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Diesen Bereich wirklich auf die Standardwerte zurücksetzen?')) return;
    setSaving(true);
    setMessage('');
    try {
      const res = await axios.post(
        `${API}/admin/content/${activeKey}/reset`,
        {},
        { headers: authHeaders }
      );
      setContent({ ...content, [activeKey]: res.data.data });
      setMessage('✅ Zurückgesetzt');
      setTimeout(() => setMessage(''), 4000);
    } catch (err) {
      console.error(err);
      setMessage('❌ Zurücksetzen fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="bg-white rounded-2xl p-8 text-center text-[#86868B]">Laden…</div>;
  }

  const Editor = EDITOR_MAP[activeKey];
  const data = content[activeKey] || {};
  const updateActive = (next) => setContent({ ...content, [activeKey]: next });

  return (
    <div className="bg-white rounded-2xl p-6 md:p-8">
      <div className="flex flex-wrap gap-2 mb-6 border-b border-[#D2D2D7] pb-4">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            onClick={() => setActiveKey(s.key)}
            data-testid={`cms-tab-${s.key}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeKey === s.key
                ? 'bg-[#0071E3] text-white'
                : 'bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E8E8ED]'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {Editor && <Editor data={data} onChange={updateActive} />}

      {message && (
        <div className="mt-6 bg-[#F5F5F7] rounded-xl p-4">
          <p className="text-[#1D1D1F]">{message}</p>
        </div>
      )}

      <div className="mt-8 flex flex-wrap gap-3">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0071E3] hover:bg-[#0077ED] text-white rounded-full h-12 px-8"
          data-testid="cms-save-button"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Speichere…' : 'Änderungen speichern'}
        </Button>
        <Button
          onClick={handleReset}
          disabled={saving}
          variant="outline"
          className="border-[#D2D2D7] text-[#86868B] hover:text-[#1D1D1F] rounded-full h-12 px-6"
          data-testid="cms-reset-button"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Auf Standard zurücksetzen
        </Button>
      </div>
    </div>
  );
};

export default ContentEditor;
